import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, normalizeStyle as _normalizeStyle, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "section lucky-numbers" }
const _hoisted_2 = {
  key: 0,
  class: "section-description"
}
const _hoisted_3 = {
  key: 2,
  class: "date-filter-section"
}
const _hoisted_4 = { class: "numbers-grid" }
const _hoisted_5 = {
  key: 3,
  class: "load-more-container"
}

import { ref, computed, nextTick } from "vue";

import LotteryCard from "./LotteryCard.vue";
import LotteryFilters from "./LotteryFilters.vue";
import NextDrawsFilter from "./NextDrawsFilter.vue";
import { iframeCommunication } from "@/utils/iframeCommunication";


const __default__ = {
  name: 'LuckyNumbersSection',
};

export default /*@__PURE__*/_defineComponent({
  ...__default__,
  props: {
    title: {},
    description: {},
    games: {},
    showDatePicker: { type: Boolean },
    dateFilterOnly: { type: Boolean },
    isFavorited: { type: Function },
    selectedDate: {},
    titleAlign: {}
  },
  emits: ["bet", "favorite", "remove", "dateChange"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

// Pagination state
const displayLimit = ref(10);

// Filter states
const searchQuery = ref('');
const selectedCountry = ref('');
const selectedDate = ref(props.selectedDate || '');
const sortBy = ref('default');

const isWithinTimeRange = (date: Date, range: string) => {
  const now = new Date();
  const drawDate = getValidDate(date);
  
  // If date is invalid, return false
  if (drawDate.getTime() === 0) return false;
  
  const hours = (drawDate.getTime() - now.getTime()) / (1000 * 60 * 60);
  
  switch (range) {
    case '1h': return hours <= 1;
    case '6h': return hours <= 6;
    case '12h': return hours <= 12;
    case '24h': return hours <= 24;
    default: return true;
  }
};

function sortGames(games: any[], sort: string) {
  switch (sort) {
    case 'time-asc':
      return [...games].sort((a, b) => {
        const dateA = getValidDate(a.cutoff || a.draw_date);
        const dateB = getValidDate(b.cutoff || b.draw_date);
        return dateA.getTime() - dateB.getTime();
      });
    case 'time-desc':
      return [...games].sort((a, b) => {
        const dateA = getValidDate(a.cutoff || a.draw_date);
        const dateB = getValidDate(b.cutoff || b.draw_date);
        return dateB.getTime() - dateA.getTime();
      });
    case 'country-asc':
      return [...games].sort((a, b) => (a.country || '').localeCompare(b.country || '', undefined, { sensitivity: 'base' }));
    case 'country-desc':
      return [...games].sort((a, b) => (b.country || '').localeCompare(a.country || '', undefined, { sensitivity: 'base' }));
    case 'name-asc':
      return [...games].sort((a, b) => (a.game_name || a.name || '').localeCompare(b.game_name || b.name || '', undefined, { sensitivity: 'base' }));
    case 'name-desc':
      return [...games].sort((a, b) => (b.game_name || b.name || '').localeCompare(a.game_name || a.name || '', undefined, { sensitivity: 'base' }));
    default:
      return games;
  }
}

// Helper function to safely parse dates
function getValidDate(dateString: any): Date {
  if (!dateString) return new Date(0); // Return epoch for null/undefined dates
  
  try {
    const dateStr = String(dateString);
    let date: Date;
    
    if (dateStr.includes('+')) {
      // UTC format like "2025-07-30 23:51:30+00:00"
      const datePart = dateStr.split('+')[0];
      date = new Date(datePart + 'Z');
    } else {
      // Fallback for other formats
      date = new Date(dateStr);
    }
    
    // Check if the date is valid
    if (isNaN(date.getTime())) {
      console.warn('Invalid date format:', dateString);
      return new Date(0); // Return epoch for invalid dates
    }
    
    return date;
  } catch (error) {
    console.warn('Error parsing date:', dateString, error);
    return new Date(0); // Return epoch for error cases
  }
}

const filteredGames = computed(() => {
  let filtered = [...props.games];

  if (searchQuery.value || selectedCountry.value || (selectedDate.value && selectedDate.value !== '')) {
    filtered = filtered.filter(draw => {
      // Apply search filter
      const matchesSearch = !searchQuery.value ||
        (draw.country && draw.country.toLowerCase().includes(searchQuery.value.toLowerCase())) ||
        (draw.game_name && draw.game_name.toLowerCase().includes(searchQuery.value.toLowerCase())) ||
        (draw.name && draw.name.toLowerCase().includes(searchQuery.value.toLowerCase()));
      
      // Apply country filter
      const matchesCountry = !selectedCountry.value || draw.country === selectedCountry.value;
      
      // Apply date filter
      const matchesDate = !selectedDate.value || selectedDate.value === '' || (() => {
        if (!draw.draw_date) return false;
        
        try {
          // Parse the draw date properly (handle UTC format)
          const drawDateString = String(draw.draw_date);
          let drawDate: Date;
          
          if (drawDateString.includes('+')) {
            // UTC format like "2025-07-30 23:51:30+00:00"
            const datePart = drawDateString.split('+')[0];
            drawDate = new Date(datePart + 'Z');
          } else {
            // Fallback for other formats
            drawDate = new Date(drawDateString);
          }
          
          // Check if the date is valid
          if (isNaN(drawDate.getTime())) {
            return false;
          }
          
          // Get the date part in YYYY-MM-DD format
          const drawDateFormatted = drawDate.toISOString().split('T')[0];
          
          return drawDateFormatted === selectedDate.value;
        } catch {
          return false;
        }
      })();
      
      return matchesSearch && matchesCountry && matchesDate;
    });
  }

  filtered = sortGames(filtered, sortBy.value);

  return filtered.slice(0, displayLimit.value);
});

const hasMoreGames = computed(() => {
  let filtered = sortGames(props.games, sortBy.value);

  if (searchQuery.value || selectedCountry.value || (selectedDate.value && selectedDate.value !== '')) {
    filtered = filtered.filter(draw => {
      // Apply search filter
      const matchesSearch = !searchQuery.value ||
        (draw.country && draw.country.toLowerCase().includes(searchQuery.value.toLowerCase())) ||
        (draw.game_name && draw.game_name.toLowerCase().includes(searchQuery.value.toLowerCase())) ||
        (draw.name && draw.name.toLowerCase().includes(searchQuery.value.toLowerCase()));
      
      // Apply country filter
      const matchesCountry = !selectedCountry.value || draw.country === selectedCountry.value;
      
      // Apply date filter
      const matchesDate = !selectedDate.value || selectedDate.value === '' || (() => {
        if (!draw.draw_date) return false;
        
        try {
          // Parse the draw date properly (handle UTC format)
          const drawDateString = String(draw.draw_date);
          let drawDate: Date;
          
          if (drawDateString.includes('+')) {
            // UTC format like "2025-07-30 23:51:30+00:00"
            const datePart = drawDateString.split('+')[0];
            drawDate = new Date(datePart + 'Z');
          } else {
            // Fallback for other formats
            drawDate = new Date(drawDateString);
          }
          
          // Check if the date is valid
          if (isNaN(drawDate.getTime())) {
            return false;
          }
          
          // Get the date part in YYYY-MM-DD format
          const drawDateFormatted = drawDate.toISOString().split('T')[0];
          
          return drawDateFormatted === selectedDate.value;
        } catch {
          return false;
        }
      })();
      
      return matchesSearch && matchesCountry && matchesDate;
    });
  }

  return filtered.length > displayLimit.value;
});

const loadMore = () => {
  displayLimit.value += 10;
  
  // Trigger immediate height update after DOM updates with a longer delay
  nextTick(() => {
    // Add extra delay to ensure all content is rendered
    setTimeout(() => {
      iframeCommunication.sendImmediateHeightUpdate();
    }, 100);
  });
};

const handleSearch = (query: string) => {
  searchQuery.value = query;
};

const handleCountryFilter = (country: string) => {
  selectedCountry.value = country;
};

const handleDateFilter = (date: string) => {
  selectedDate.value = date;
  emit('dateChange', date); // Emit dateChange event
};

const handleSort = (sort: string) => {
  sortBy.value = sort;
};

const handleBet = (game: any) => {
  emit('bet', game);
};

const handleFavorite = (game: any) => {
  emit('favorite', game);
};

const handleRemove = (game: any) => {
  emit('remove', game);
};


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("section", _hoisted_1, [
    _createElementVNode("h2", {
      class: "section-title medium",
      style: _normalizeStyle({ textAlign: _ctx.titleAlign || 'center' })
    }, _toDisplayString(_ctx.title), 5),
    (_ctx.description)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createElementVNode("p", null, _toDisplayString(_ctx.description), 1)
        ]))
      : _createCommentVNode("", true),
    (!_ctx.dateFilterOnly)
      ? (_openBlock(), _createBlock(LotteryFilters, {
          key: 1,
          onSearch: handleSearch,
          onFilterCountry: handleCountryFilter,
          onFilterDate: handleDateFilter,
          onSort: handleSort,
          "show-date-picker": _ctx.showDatePicker
        }, null, 8, ["show-date-picker"]))
      : (_openBlock(), _createElementBlock("div", _hoisted_3, [
          _createVNode(LotteryFilters, {
            onSearch: handleSearch,
            onFilterCountry: handleCountryFilter
          }),
          _createVNode(NextDrawsFilter, {
            modelValue: selectedDate.value,
            "onUpdate:modelValue": [
              _cache[0] || (_cache[0] = ($event: any) => ((selectedDate).value = $event)),
              handleDateFilter
            ],
            "show-date-picker": _ctx.showDatePicker
          }, null, 8, ["modelValue", "show-date-picker"])
        ])),
    _createElementVNode("div", _hoisted_4, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredGames.value, (game) => {
        return (_openBlock(), _createBlock(LotteryCard, {
          key: game.id,
          lottery: game,
          layout: "horizontal",
          onBet: ($event: any) => (handleBet(game)),
          onFavorite: ($event: any) => (handleFavorite(game)),
          onRemove: ($event: any) => (handleRemove(game)),
          isFavorited: _ctx.isFavorited
        }, null, 8, ["lottery", "onBet", "onFavorite", "onRemove", "isFavorited"]))
      }), 128))
    ]),
    (hasMoreGames.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
          _createElementVNode("button", {
            class: "load-more-button",
            onClick: loadMore
          }, _toDisplayString(_ctx.$t('forms.loadMoreNumberGames')), 1)
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})